import { Box } from '@mui/system';
import useStyles from './styles';
import useCommonStyles from '../../commonStyles';
import Option from './Option';
import AccordionSettings from './AccordionSettings';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { useState, useCallback } from 'react';
import SearchCommandDialog from './SearchCommandDialog';
import { useSearchShortcut } from '../../CustomHooks/useSearchShortcut';

const Sidebar = ({ sidebarConfig, changeSetting, currentSetting, system }) => {
	const classes = useStyles();
	const commonStyleClasses = useCommonStyles();
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const [sidebarOptionHovered, setSidebarOptionHovered] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);

	const isSidebarCollapsed = sidebarCollapsed && !sidebarOptionHovered;

	const sidebarClasses = isSidebarCollapsed
		? `${classes.sidebar} ${classes.sidebarCollapsed}`
		: classes.sidebar;

	const handleOpenSearch = useCallback(() => setSearchOpen(true), []);
	const handleCloseSearch = useCallback(() => setSearchOpen(false), []);

	// Cmd+K / Ctrl+K keyboard shortcut
	useSearchShortcut(handleOpenSearch);

	return (
		<Box className={sidebarClasses}>
			<Box className={classes.sidebarCollapseIconContainer} sx={{ width: "100%" }}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: "space-between",
						alignItems: 'center',
						gap: '4px'
					}}
				>
					<Box
						onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
						sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
					>
						<MenuIcon className={commonStyleClasses.icon} />
					</Box>
					{!isSidebarCollapsed && (
						<Tooltip title="Search Features (⌘/ctrl + K)" arrow>
							<IconButton
								onClick={handleOpenSearch}
								size="small"
								sx={{
									color: 'inherit',
									opacity: 0.7,
									transition: 'opacity 0.2s',
									'&:hover': { opacity: 1 }
								}}
								aria-label="Search features"
							>
								<SearchIcon fontSize="large" />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			</Box>
			<SidebarContent
				sidebarConfig={sidebarConfig}
				changeSetting={changeSetting}
				currentSetting={currentSetting}
				system={system}
				sidebarCollapsed={sidebarCollapsed}
				sidebarOptionHovered={sidebarOptionHovered}
				setSidebarOptionHovered={setSidebarOptionHovered}
			/>
			<SearchCommandDialog
				open={searchOpen}
				onClose={handleCloseSearch}
				sidebarConfig={sidebarConfig}
				changeSetting={changeSetting}
			/>
		</Box>
	);
};

const SidebarContent = ({
	sidebarConfig,
	changeSetting,
	currentSetting,
	system,
	sidebarCollapsed,
	sidebarOptionHovered,
	setSidebarOptionHovered
}) => {
	const classes = useStyles();
	const isSidebarCollapsed = sidebarCollapsed && !sidebarOptionHovered;

	const sidebarButtonContainerClasses = isSidebarCollapsed
		? `${classes.sidebarButtonContainer} ${classes.sidebarButtonContainerHidden}`
		: classes.sidebarButtonContainer;
	return (
		<Box
			onMouseOver={() => setSidebarOptionHovered(true)}
			onMouseLeave={() => setSidebarOptionHovered(false)}
		>
			{sidebarConfig.map((configData) => {
				if (configData.isButton) {
					return <Box className={sidebarButtonContainerClasses}>{configData.button}</Box>;
				}
				if (configData.isAccordion) {
					return (
						<AccordionSettings
							key={configData.name}
							label={configData.label}
							name={configData.name}
							system={system}
							icon={configData.icon}
							sidebarCollapsed={sidebarCollapsed}
							sidebarOptionHovered={sidebarOptionHovered}
							currentSetting={currentSetting}
							childSettings={configData.settings}
						>
							<SidebarContent
								sidebarConfig={configData.settings}
								changeSetting={changeSetting}
								currentSetting={currentSetting}
								system={system}
								sidebarCollapsed={sidebarCollapsed}
								sidebarOptionHovered={sidebarOptionHovered}
								setSidebarOptionHovered={setSidebarOptionHovered}
							/>
						</AccordionSettings>
					);
				}
				return (
					<Option
						selected={currentSetting === configData.settingName}
						label={configData.label}
						settingName={configData.settingName}
						changeSetting={changeSetting}
						icon={configData.icon}
						system={system}
						sidebarCollapsed={sidebarCollapsed}
						sidebarOptionHovered={sidebarOptionHovered}
					/>
				);
			})}
		</Box>
	);
};

export default Sidebar;

